import { useEffect, useRef } from "react";

const MAX_KEY_GAP_MS = 150;
const MIN_SCAN_LENGTH = 18;

function isEditableTarget(target) {
    return target instanceof Element && (
        target.matches("input, textarea, select") || target.closest("[contenteditable='true']")
    );
}

export default function useMedicationScanner({ enabled, onScan }) {
    const onScanRef = useRef(onScan);
    onScanRef.current = onScan;

    useEffect(() => {
        if (!enabled) return undefined;
        let buffer = "";
        let lastKeyAt = 0;

        const reset = () => {
            buffer = "";
            lastKeyAt = 0;
        };
        const submit = rawCode => {
            const value = rawCode.trim();
            reset();
            if (value.length < MIN_SCAN_LENGTH) return;
            void onScanRef.current(value);
        };
        const onKeyDown = event => {
            if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
            if (isEditableTarget(event.target)) {
                reset();
                return;
            }
            const now = Date.now();
            if (lastKeyAt && now - lastKeyAt > MAX_KEY_GAP_MS) buffer = "";
            if (event.key === "Enter") {
                if (buffer.length >= MIN_SCAN_LENGTH) event.preventDefault();
                submit(buffer);
                return;
            }
            if (event.key.length === 1) {
                buffer += event.key;
                lastKeyAt = now;
            }
        };
        const onPaste = event => {
            if (isEditableTarget(event.target)) return;
            const value = event.clipboardData?.getData("text") || "";
            if (value.trim().length < MIN_SCAN_LENGTH) return;
            event.preventDefault();
            submit(value);
        };

        window.addEventListener("keydown", onKeyDown, true);
        window.addEventListener("paste", onPaste, true);
        return () => {
            window.removeEventListener("keydown", onKeyDown, true);
            window.removeEventListener("paste", onPaste, true);
        };
    }, [enabled]);
}
